/* ===========================================================================
   CALMY PREDICTIVE SEARCH
   ===========================================================================

   Native Shopify predictive search (/search/suggest.json). No app, no third
   party, no index to keep in sync.

   ONE THING TO KNOW ABOUT TESTING THIS: the suggest endpoint returns 401
   through `shopify theme dev`, because a Theme Access token does not carry
   storefront session scope. It works on the real storefront. That is why the
   catch below offers the full search page rather than dead-ending — the same
   fallback also covers a password-protected storefront, a rate limit, or the
   customer being offline.

   The panel open/close, focus trap, Esc and scroll lock all live in
   calmy-core.js and are not duplicated here. This file owns querying,
   rendering, keyboard traversal within the results, and recent searches.
   =========================================================================== */
(function () {
  /* Editable wording. Sources: Theme settings > "Text: ..." groups,
     serialised by snippets/calmy-strings.liquid. The second argument is
     the wording this file shipped with and is used when the snippet is
     absent or the setting is blank. */
  var CT = function (k, f) { return window.CalmyT ? window.CalmyT(k, f) : f; };

  "use strict";

  var RECENT_KEY = "calmy:recent-searches";
  var RECENT_MAX = 5;
  var DEBOUNCE = 200;
  var MIN_CHARS = 2;

  function esc(s) {
    return String(s == null ? "" : s).replace(/[&<>"']/g, function (c) {
      return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c];
    });
  }

  /* Shopify image URLs take a width parameter; asking for 96 rather than the
     original keeps a 48px thumbnail at 48px on a 2x screen. */
  function sized(url, w) {
    if (!url) return "";
    return url.indexOf("?") > -1 ? url + "&width=" + w : url + "?width=" + w;
  }

  function money(cents) {
    if (window.Calmy && typeof window.Calmy.money === "function") return window.Calmy.money(cents);
    return "";
  }

  /* Predictive search does NOT return cents.
     /search/suggest.json gives price as a DECIMAL STRING — "34.00" — while
     Calmy.money() takes cents and divides by 100. Passing one to the other
     rendered $34.00 as "$0.34" on every row. Convert once, here. */
  function priceOf(v) {
    if (v == null || v === "") return "";
    var n = typeof v === "string" ? parseFloat(v) : v;
    if (!isFinite(n)) return "";
    return money(Math.round(n * 100));
  }

  function num(v) {
    var n = typeof v === "string" ? parseFloat(v) : v;
    return isFinite(n) ? n : 0;
  }

  /* ---------------- recent searches ---------------- */

  function readRecent() {
    try {
      var raw = localStorage.getItem(RECENT_KEY);
      var list = raw ? JSON.parse(raw) : [];
      return Array.isArray(list) ? list.filter(function (x) { return typeof x === "string" && x; }) : [];
    } catch (e) {
      /* Private mode, quota, or a corrupted value. Recent searches are a
         convenience; losing them must never break the panel. */
      return [];
    }
  }

  function pushRecent(q) {
    if (!q || q.length < MIN_CHARS) return;
    try {
      var list = readRecent().filter(function (x) { return x.toLowerCase() !== q.toLowerCase(); });
      list.unshift(q);
      localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, RECENT_MAX)));
    } catch (e) { /* see above */ }
  }

  function clearRecent() {
    try { localStorage.removeItem(RECENT_KEY); } catch (e) {}
  }

  function renderRecent(slot) {
    if (!slot) return;
    var list = readRecent();
    if (!list.length) { slot.hidden = true; slot.innerHTML = ""; return; }
    slot.hidden = false;
    slot.innerHTML =
      '<section class="csearch__grp">' +
        '<p class="csearch__lab csearch__lab--row">Recent' +
          '<button type="button" class="csearch__clearall" data-calmy-recent-clear>Clear</button>' +
        "</p>" +
        '<div class="csearch__pills">' +
          list.map(function (q) {
            return '<button type="button" class="csearch__pill" data-calmy-recent-q="' + esc(q) + '">' + esc(q) + "</button>";
          }).join("") +
        "</div>" +
      "</section>";
  }

  /* ---------------- rendering ---------------- */

  function productRow(p, id) {
    var img = (p.featured_image && p.featured_image.url) || p.image || "";
    /* The variant line: the first variant's title is "Silver / 7" style, which
       is the finish and size the customer is actually choosing between. Default
       Title carries no information and is dropped. */
    var sub = "";
    if (p.variants && p.variants.length && p.variants[0].title && p.variants[0].title !== "Default Title") {
      sub = p.variants[0].title;
    }
    var price = priceOf(p.price);
    /* Numeric comparison, not string: compare_at_price_max comes back as
       "0.00" when there is no sale, and "0.00" > "34.00" is false only by
       accident of lexical ordering. */
    var was = num(p.compare_at_price_max) > num(p.price) ? priceOf(p.compare_at_price_max) : "";
    /* A CARD, NOT A ROW.

       The panel is a grid of products now, and the empty state is rendered by
       Liquid as the collection card (.ccard--plain). This builds the same
       shape from suggest.json so typing swaps the contents of the grid and not
       its layout: image on top at 4:5, then the title, then the price. The
       words sit outside the image, so nothing can cover them.

       role="option" / id / tabindex are kept exactly as they were: the
       keyboard navigation below walks these ids, and losing them would break
       arrow-key selection. */
    return (
      '<div class="ccard calmy ccard--plain">' +
        '<div class="ccard__inner">' +
          '<a class="ccard__link" role="option" id="' + id + '" href="' + esc(p.url) + '" tabindex="-1">' +
            '<span class="calmy-sr">' + esc(p.title) + "</span>" +
          "</a>" +
          '<span class="ccard__media">' +
            (img
              ? '<img class="ccard__img" src="' + esc(sized(img, 600)) + '" alt="" loading="lazy">'
              : '<span class="ccard__empty"></span>') +
            cardAdd(p) +
          "</span>" +
          '<span class="ccard__foot">' +
            '<span class="ccard__t">' + esc(p.title) + "</span>" +
            '<span class="ccard__sub">' + (sub ? esc(sub) : "") + "</span>" +
            '<span class="ccard__p">' +
              (was ? '<del class="ccard__was">' + esc(was) + "</del>" : "") +
              '<span class="ccard__now">' + esc(price) + "</span>" +
            "</span>" +
          "</span>" +
        "</div>" +
      "</div>"
    );
  }

  /* THE CART BUTTON ON A CARD BUILT HERE.

     suggest.json returns an empty variants array, so this markup cannot know
     whether the product has one variant or five - it passes the handle and
     calmy-cards.js asks /products/<handle>.js on the first press, which is the
     only moment the answer is actually needed. A product the endpoint already
     reports as unavailable gets no handle and simply opens its page. */
  /* The same cart glyph snippets/calmy-icon.liquid draws, copied rather than
     fetched: this string is built while the customer is typing. */
  var CART_SVG =
    '<svg class="cico" viewBox="0 0 24 24" aria-hidden="true" focusable="false">' +
    '<path d="M2.75 4h2.2l2.3 10.4h9.9l2.1-7.4H6.1"/><circle cx="9.5" cy="18.75" r="1.5"/>' +
    '<circle cx="16.5" cy="18.75" r="1.5"/></svg>';

  function cardAdd(p) {
    var live = p.available !== false;
    return (
      '<button type="button" class="ccard__add' + (live ? "" : " ccard__add--choose") + '"' +
        " data-calmy-cardadd" +
        (live ? ' data-product-handle="' + esc(p.handle) + '"' : "") +
        ' data-product-url="' + esc(p.url) + '"' +
        ' aria-label="' + esc((live ? "Add to cart: " : "View product: ") + p.title) + '">' +
        '<span class="ccard__add-g ccard__add-g--rest" aria-hidden="true">' + CART_SVG + "</span>" +
        '<span class="ccard__add-g ccard__add-g--done" aria-hidden="true"><svg class="cico" viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7.5"/></svg></span>' +
        '<span class="ccard__add-g ccard__add-g--busy" aria-hidden="true"><svg class="cico" viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="8.5" opacity=".25"/><path d="M12 3.5a8.5 8.5 0 0 1 8.5 8.5"/></svg></span>' +
      "</button>"
    );
  }

  function plainRow(item, id) {
    return (
      '<a class="csr csr--plain" role="option" id="' + id + '" href="' + esc(item.url) + '" tabindex="-1">' +
        '<span class="csr__b"><span class="csr__t">' + esc(item.title) + "</span></span>" +
      "</a>"
    );
  }

  function group(label, inner, grid) {
    if (!inner) return "";
    /* Products go in the grid; collections and pages stay as plain rows,
       because they are navigation rather than merchandise and a four-column
       grid of two text links reads as a mistake. */
    var open = grid
      ? '<section class="csearch__grp csearch__grp--grid"><p class="csearch__lab">' + esc(label) + '</p><div class="cgrid csearch__grid">'
      : '<section class="csearch__grp"><p class="csearch__lab">' + esc(label) + '</p>';
    var close = grid ? "</div></section>" : "</section>";
    return open + inner + close;
  }

  /* ---------------- main ---------------- */

  function init() {
    var box = document.querySelector("[data-calmy-search]");
    if (!box || box.__calmySearchV2) return;
    box.__calmySearchV2 = true;

    var input = box.querySelector("[data-calmy-search-input]");
    var out = box.querySelector("[data-calmy-search-results]");
    var status = box.querySelector("[data-calmy-search-status]");
    var clearBtn = box.querySelector("[data-calmy-search-clear]");
    var form = box.querySelector("form");
    if (!input || !out) return;

    var defaultBlock = out.querySelector("[data-calmy-search-default]");
    var defaultHTML = defaultBlock ? defaultBlock.outerHTML : "";
    var recentSlot = out.querySelector("[data-calmy-recent]");

    var limitP = parseInt(box.getAttribute("data-limit-products"), 10) || 6;
    var limitO = parseInt(box.getAttribute("data-limit-other"), 10) || 3;

    var timer = null;
    var controller = null;
    var cursor = -1;

    function options() { return [].slice.call(out.querySelectorAll('[role="option"]')); }

    function highlight(i) {
      var opts = options();
      if (!opts.length) return;
      if (i < 0) i = opts.length - 1;
      if (i >= opts.length) i = 0;
      cursor = i;
      opts.forEach(function (o, n) { o.classList.toggle("is-active", n === i); });
      var el = opts[i];
      input.setAttribute("aria-activedescendant", el.id || "");
      /* Keep the highlighted row in view without yanking the whole panel. */
      var r = el.getBoundingClientRect();
      var b = out.getBoundingClientRect();
      if (r.bottom > b.bottom) out.scrollTop += r.bottom - b.bottom + 8;
      else if (r.top < b.top) out.scrollTop -= b.top - r.top + 8;
    }

    function clearHighlight() {
      cursor = -1;
      options().forEach(function (o) { o.classList.remove("is-active"); });
      input.removeAttribute("aria-activedescendant");
    }

    function say(msg) { if (status) status.textContent = msg; }

    function showDefault() {
      out.innerHTML = defaultHTML;
      recentSlot = out.querySelector("[data-calmy-recent]");
      renderRecent(recentSlot);
      input.setAttribute("aria-expanded", "false");
      clearHighlight();
      say("");
    }

    /* PHONE: THE PANEL IS A BAR.

       The spec asks for a search bar on a phone and nothing else - no browse
       links, no tiles, no suggested products - with Enter, the keyboard's
       Search key or the magnifier taking the shopper to the real results page.
       Predictive results are a desktop affordance here, so on a narrow screen
       nothing is fetched at all: no request, no spinner, no list to hide. */
    function barOnly() {
      return window.matchMedia && window.matchMedia("(max-width: 999px)").matches;
    }

    function render(data, q) {
      var res = (data && data.resources && data.resources.results) || {};
      var products = (res.products || []).slice(0, limitP);
      var collections = (res.collections || []).slice(0, limitO);
      var pages = (res.pages || []).slice(0, limitO);
      var articles = (res.articles || []).slice(0, limitO);
      var total = products.length + collections.length + pages.length + articles.length;

      if (!total) {
        /* No apology copy, per the brief — one line of fact, then the browse
           links as a way back. */
        out.innerHTML =
          '<p class="csearch__msg">No matches for “' + esc(q) + '”.</p>' + defaultHTML;
        recentSlot = out.querySelector("[data-calmy-recent]");
        renderRecent(recentSlot);
        input.setAttribute("aria-expanded", "true");
        clearHighlight();
        say(CT("search_no_results", "No results for \u201c[query]\u201d").split("[query]").join(q));
        return 0;
      }

      var n = 0;
      var html = "";
      html += group("Products", products.map(function (p) { return productRow(p, "csr-" + n++); }).join(""), true);
      html += group("Collections", collections.map(function (c) { return plainRow(c, "csr-" + n++); }).join(""));
      var other = pages.concat(articles);
      html += group("Pages", other.slice(0, limitO).map(function (a) { return plainRow(a, "csr-" + n++); }).join(""));
      html +=
        '<a class="csearch__all" role="option" id="csr-' + n + '" tabindex="-1" href="' +
        esc(window.Shopify && window.Shopify.routes ? window.Shopify.routes.root + "search" : "/search") +
        "?q=" + encodeURIComponent(q) + '">' + esc(CT("search_view_all_for", "View all results for “[query]”").split("[query]").join(q)) + "</a>";

      out.innerHTML = html;
      out.scrollTop = 0;
      input.setAttribute("aria-expanded", "true");
      clearHighlight();
      say(total + (total === 1 ? " result" : " results") + " for " + q);
      return total;
    }

    function query(q) {
      /* Nothing is fetched on a phone - see barOnly(). The results element is
         emptied so a query typed at a desktop width and then resized cannot
         leave a list behind under the bar. */
      if (barOnly()) {
        if (controller) controller.abort();
        out.innerHTML = "";
        input.setAttribute("aria-expanded", "false");
        return;
      }
      if (controller) controller.abort();
      controller = new AbortController();
      var url =
        "/search/suggest.json?q=" + encodeURIComponent(q) +
        "&resources[type]=product,collection,page,article" +
        "&resources[limit]=" + Math.max(limitP, limitO) +
        "&resources[options][unavailable_products]=last";
      fetch(url, { signal: controller.signal, headers: { Accept: "application/json" } })
        .then(function (r) {
          if (!r.ok) throw new Error("HTTP " + r.status);
          return r.json();
        })
        .then(function (d) {
          /* Recorded here, not only on Enter. A query you typed and saw results
             for is a query you searched — most people click a result rather
             than pressing Enter, and recording only on Enter meant "recent"
             stayed empty for them. Debounced and >=2 chars, so partial
             keystrokes never land.

             Only when it FOUND something: offering a failed search back as a
             suggestion sends the customer down the same dead end twice. */
          if (render(d, q) > 0) pushRecent(q);
        })
        .catch(function (err) {
          if (err && err.name === "AbortError") return;
          /* See the file header. Offer the real search page rather than
             stopping the customer at a broken panel. */
          out.innerHTML =
            '<p class="csearch__msg">' + esc(CT("search_unavailable", "Suggestions are unavailable right now.")) + '</p>' +
            '<a class="csearch__all" role="option" id="csr-0" tabindex="-1" href="/search?q=' +
            encodeURIComponent(q) + '">' + esc(CT("search_search_for", "Search for “[query]”").split("[query]").join(q)) + "</a>";
          input.setAttribute("aria-expanded", "true");
          clearHighlight();
          say(CT("search_suggest_off", "Suggestions unavailable. Press Enter to search."));
        });
    }

    input.addEventListener("input", function () {
      var q = input.value.trim();
      if (clearBtn) clearBtn.hidden = !q;
      clearTimeout(timer);
      if (q.length < MIN_CHARS) {
        if (controller) controller.abort();
        showDefault();
        return;
      }
      timer = setTimeout(function () { query(q); }, DEBOUNCE);
    });

    /* Enter with a row highlighted follows that row; Enter with nothing
       highlighted submits the form, which is the full search page. */
    input.addEventListener("keydown", function (e) {
      if (e.key === "ArrowDown") { e.preventDefault(); highlight(cursor + 1); }
      else if (e.key === "ArrowUp") { e.preventDefault(); highlight(cursor - 1); }
      else if (e.key === "Enter") {
        var opts = options();
        if (cursor > -1 && opts[cursor]) {
          e.preventDefault();
          pushRecent(input.value.trim());
          window.location.href = opts[cursor].getAttribute("href");
        } else {
          pushRecent(input.value.trim());
        }
      } else if (e.key === "Escape") {
        if (input.value) { e.stopPropagation(); input.value = ""; if (clearBtn) clearBtn.hidden = true; showDefault(); }
        /* Empty input: let calmy-core.js handle Esc and close the panel. */
      }
    });

    if (form) form.addEventListener("submit", function () { pushRecent(input.value.trim()); });

    if (clearBtn) clearBtn.addEventListener("click", function () {
      input.value = ""; clearBtn.hidden = true; showDefault(); input.focus();
    });

    /* Recent pills and the clear-all live inside replaced markup, so they are
       handled by delegation rather than bound per render. */
    out.addEventListener("click", function (e) {
      var clear = e.target.closest("[data-calmy-recent-clear]");
      if (clear) { e.preventDefault(); clearRecent(); renderRecent(out.querySelector("[data-calmy-recent]")); return; }
      var pill = e.target.closest("[data-calmy-recent-q]");
      if (pill) {
        e.preventDefault();
        input.value = pill.getAttribute("data-calmy-recent-q");
        if (clearBtn) clearBtn.hidden = false;
        input.focus();
        query(input.value);
      }
    });

    out.addEventListener("mousemove", function (e) {
      var row = e.target.closest('[role="option"]');
      if (!row) return;
      var i = options().indexOf(row);
      if (i > -1 && i !== cursor) highlight(i);
    });

    renderRecent(recentSlot);
  }

  /* ---------------- global shortcuts ---------------- */

  function shortcuts() {
    if (document.__calmySearchKeys) return;
    document.__calmySearchKeys = true;
    document.addEventListener("keydown", function (e) {
      var t = e.target;
      var typing = t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable);
      var meta = (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k";
      var slash = e.key === "/" && !typing && !e.metaKey && !e.ctrlKey && !e.altKey;
      if (!meta && !slash) return;
      var panel = document.querySelector("[data-calmy-search]");
      if (!panel) return;
      e.preventDefault();
      /* The trigger matters: C.open(selector, trigger) stores it and C.close()
         returns focus to it. Opening from the keyboard without one left focus
         on <body> after Esc, which drops a keyboard user at the top of the
         document instead of back at the search button. */
      var trigger = document.querySelector('[data-calmy-open="[data-calmy-search]"]');
      if (window.Calmy && typeof window.Calmy.open === "function") window.Calmy.open("[data-calmy-search]", trigger);
      else if (trigger) trigger.click();
      var input = panel.querySelector("[data-calmy-search-input]");
      if (input) setTimeout(function () { input.focus(); input.select(); }, 60);
    });
  }

  function boot() { init(); shortcuts(); }
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot);
  else boot();
  document.addEventListener("shopify:section:load", boot);
})();
